import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import type { BlogPost } from "@ckgo2/shared/types";
import { BLOG_CATEGORIES } from "@ckgo2/shared/types";
import { BLOG_CATEGORY_LABELS as CATEGORY_LABELS } from "@ckgo2/shared/utils";
import { Plus, Search, Newspaper, Edit, Trash2, MapPin, Clock, Star, Copy } from "lucide-react";

interface BlogPostWithDest extends BlogPost {
  destinationName?: string;
}

const STATUS_COLORS: Record<string, string> = {
  published: "badge-primary",
  draft: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
  archived: "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400",
};

export default function BlogPostsPage() {
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["blogPosts"],
    queryFn: () => api.get<ApiResponse<BlogPostWithDest[]>>("/api/admin/blog"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/blog/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["blogPosts"] });
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/blog/${id}/duplicate`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["blogPosts"] });
    },
  });

  const handleDelete = (id: string, title: string) => {
    if (confirm(`Are you sure you want to delete "${title}"? This action cannot be undone.`)) {
      deleteMutation.mutate(id);
    }
  };

  const posts = data?.data || [];

  const filteredPosts = posts.filter((p) => {
    const matchesSearch =
      p.title.toLowerCase().includes(search.toLowerCase()) ||
      p.slug.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !categoryFilter || p.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  if (error) {
    return (
      <div className="card text-error-600 text-center">
        Error loading blog posts. Please try again.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Blog</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">Manage blog posts and articles</p>
        </div>
        <Link to="/blog/new" className="btn-primary">
          <Plus className="h-5 w-5" />
          New post
        </Link>
      </div>

      {/* Filters */}
      <div className="card flex flex-col gap-4 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search posts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-10"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="input w-full sm:w-48"
        >
          <option value="">All categories</option>
          {BLOG_CATEGORIES.map((cat) => (
            <option key={cat} value={cat}>
              {CATEGORY_LABELS[cat] || cat}
            </option>
          ))}
        </select>
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
        </div>
      ) : filteredPosts.length === 0 ? (
        <div className="card flex h-64 flex-col items-center justify-center text-gray-500">
          <Newspaper className="mb-2 h-12 w-12 text-gray-300" />
          <p>No blog posts found</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredPosts.map((post) => (
            <div key={post.id} className="card overflow-hidden p-0">
              {/* Image */}
              <div className="aspect-[3/2] bg-gray-100 dark:bg-gray-800">
                {post.featuredImage ? (
                  <img
                    src={post.featuredImage}
                    alt={post.title}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center">
                    <Newspaper className="h-16 w-16 text-gray-300" />
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="truncate font-semibold text-gray-900 dark:text-white">
                      {post.title}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">/{post.slug}</p>
                  </div>
                  <span
                    className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[post.status] || ""}`}
                  >
                    {post.status}
                  </span>
                </div>

                {/* Meta */}
                <div className="mt-3 flex flex-wrap gap-2 text-sm">
                  <span className="bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 rounded px-2 py-0.5 text-xs font-medium">
                    {CATEGORY_LABELS[post.category] || post.category}
                  </span>
                  {post.readTime && (
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                      <Clock className="h-3 w-3" />
                      {post.readTime} min
                    </span>
                  )}
                  {post.isFeatured && (
                    <span className="flex items-center gap-1 text-xs text-amber-600">
                      <Star className="h-3 w-3" />
                      Featured
                    </span>
                  )}
                </div>

                {post.destinationName && (
                  <div className="mt-2 flex items-center gap-1 text-sm text-gray-500">
                    <MapPin className="h-3.5 w-3.5" />
                    {post.destinationName}
                  </div>
                )}

                {post.authorName && <p className="mt-1 text-xs text-gray-400">{post.authorName}</p>}

                {/* Actions */}
                <div className="mt-4 flex items-center justify-end gap-2 border-t border-gray-100 pt-4 dark:border-gray-700">
                  <Link to={`/blog/${post.id}`} className="btn-ghost btn-sm">
                    <Edit className="h-4 w-4" />
                    Edit
                  </Link>
                  <button
                    onClick={() => duplicateMutation.mutate(post.id)}
                    disabled={duplicateMutation.isPending}
                    className="btn-ghost btn-sm"
                    title="Duplicate"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(post.id, post.title)}
                    disabled={deleteMutation.isPending}
                    className="btn-ghost btn-sm text-error-600 hover:bg-error-50 hover:text-error-700 dark:text-error-400 dark:hover:bg-error-900/20"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
